import type {
  IResource,
  ISchedule,
  ISlot,
  ScheduleRequestUpdate
} from "@gettactic/api";
import { dateStringToLocalTime } from "@gettactic/helpers/src/date-time/dateStringToLocalTime";
import { formatInTimeZone } from "date-fns-tz";

const WALL_CLOCK_FORMAT = "yyyy-MM-dd'T'HH:mm:ss'Z'";

const isActiveSlot = (slot: ISlot) =>
  slot.status === "accepted" || slot.status === "pending";

/**
 * A wall clock time as the API reads it in the office's time zone. Tessa
 * proposes times like 2026-10-02T09:00 or 2026-10-02T09:00:00.
 */
export const wallClockFromProposal = (value: string): string => {
  const local = value.replace("Z", "").slice(0, 19);
  return `${local.length === 16 ? `${local}:00` : local}Z`;
};

/**
 * A slot's time as the wall clock time in the office's time zone. Tessa sends
 * slot times as the browser's wall clock with a trailing Z (not UTC), so the
 * Z is dropped to read the actual moment before moving it to the office clock.
 */
export const wallClockInTimeZone = (value: string, timeZone: string): string =>
  formatInTimeZone(
    new Date(dateStringToLocalTime(value)),
    timeZone,
    WALL_CLOCK_FORMAT
  );

/**
 * The day the move's map shows: the proposed day (office wall clock), else the
 * reservation's current day on the office's clock (its start is the browser's
 * wall clock with a trailing Z).
 */
export const moveMapDay = (
  proposedStart: string | undefined,
  scheduleStart: string,
  timeZone: string | undefined
): string =>
  (
    proposedStart ??
    (timeZone ? wallClockInTimeZone(scheduleStart, timeZone) : scheduleStart)
  ).slice(0, 10);

export interface ReservationChange {
  resource_id?: string;
  start?: string;
  end?: string;
}

/**
 * New times change a reservation only as a pair: a start without an end (or
 * the other way around) could end before it starts, so it isn't applied.
 */
export const hasHalfTimeChange = (change: ReservationChange) =>
  !!change.start !== !!change.end;

/**
 * The update that moves a reservation: every attendee keeps their place, the
 * slots on the reservation's resource move to the new one, and the times
 * change only when both a new start and end are proposed.
 */
export const buildMovePayload = (
  schedule: ISchedule,
  change: ReservationChange,
  timeZone: string
): ScheduleRequestUpdate => {
  const newTimes =
    change.start && change.end
      ? {
          start: wallClockFromProposal(change.start),
          end: wallClockFromProposal(change.end)
        }
      : null;
  return {
    schedule_slots: schedule.slots.filter(isActiveSlot).map((slot) => ({
      user_id: slot.user.id,
      resource_id:
        change.resource_id && slot.resource.id === schedule.resource.id
          ? change.resource_id
          : slot.resource.id,
      start: newTimes?.start ?? wallClockInTimeZone(slot.start, timeZone),
      end: newTimes?.end ?? wallClockInTimeZone(slot.end, timeZone),
      time_zone: timeZone
    }))
  };
};

export interface RoomComparison {
  fromSeats: number;
  toSeats: number;
  fromAmenities: string[];
  toAmenities: string[];
}

/** Seats and equipment of both rooms, when a meeting moves to another room. */
export const compareRooms = (
  from: IResource | undefined,
  to: IResource | undefined,
  amenities: Record<string, string[]> | undefined
): RoomComparison | null => {
  if (!from || !to || from.id === to.id) return null;
  if (from.type !== "meeting_room" || to.type !== "meeting_room") return null;
  return {
    fromSeats: from.total_slots,
    toSeats: to.total_slots,
    fromAmenities: amenities?.[from.id] ?? [],
    toAmenities: amenities?.[to.id] ?? []
  };
};

export interface ReleaseProgress {
  released: string[];
  deskReleased: boolean;
}

// Adds one Release attempt to what the card already released. The card is
// complete only when everything attempted this time succeeded, so failed
// reservations (or the desk) stay on the card to retry.
export const nextReleaseProgress = (
  previous: ReleaseProgress,
  attempt: {
    attempted: string[];
    succeeded: string[];
    deskAttempted: boolean;
    deskSucceeded: boolean;
  }
): ReleaseProgress & { complete: boolean } => {
  const released = [
    ...previous.released,
    ...attempt.succeeded.filter((id) => !previous.released.includes(id))
  ];
  const deskReleased = previous.deskReleased || attempt.deskSucceeded;
  const failed =
    attempt.succeeded.length < attempt.attempted.length ||
    (attempt.deskAttempted && !attempt.deskSucceeded);
  return { released, deskReleased, complete: !failed };
};
